"use client";

import React, { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import "./digital-engineering.css";

/* Images — public/assets/images/ se serve hoti hain */
const IMG = "/assets/images";
/* file names me space / special characters ho sakte hain, isliye encode karke path banate hain */
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("digital-engineering-hero.jpg");
const imgBuild = img("learn-collaborate 1.jpg");
const imgCta = img("lets-banner.jpg");

/* All styling lives in digital-engineering.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--de-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "code" | "refresh" | "nodes" | "window" | "shieldcheck" | "gear"
  | "flow" | "people" | "bars" | "bolt" | "cube" | "db" | "doc"
  | "user" | "clock" | "trend" | "globe" | "briefcase" | "cloud"
  | "shield" | "link" | "target" | "mobile" | "truck";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    code: (<><path d="m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16" /></>),
    refresh: (<><path d="M20 12a8 8 0 0 0-14.5-4.7L4 9M4 4v5h5" /><path d="M4 12a8 8 0 0 0 14.5 4.7L20 15M20 20v-5h-5" /></>),
    nodes: (<><circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="6" r="2.2" /><circle cx="6" cy="18" r="2.2" /><circle cx="18" cy="18" r="2.2" /><circle cx="12" cy="12" r="2.4" /><path d="m7.6 7.6 2.7 2.7M16.4 7.6l-2.7 2.7M7.6 16.4l2.7-2.7M16.4 16.4l-2.7-2.7" /></>),
    window: (<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M7 6.5h.01M10 6.5h.01" /></>),
    shieldcheck: (<><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" /><path d="m9 12 2.2 2.2L15.5 10" /></>),
    gear: (<><circle cx="12" cy="12" r="3" /><path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" /></>),
    flow: (<><circle cx="6" cy="5" r="2" /><circle cx="18" cy="12" r="2" /><circle cx="6" cy="19" r="2" /><path d="M6 7v3a2 2 0 0 0 2 2h8M6 12v5" /></>),
    people: (<><circle cx="9" cy="8" r="3" /><path d="M3 20c.4-3.6 2.6-5.4 6-5.4s5.6 1.8 6 5.4M16 5.5a3 3 0 0 1 0 5.8M18 14.8c1.8.6 2.8 2.3 3 5.2" /></>),
    bars: <path d="M5 20V12M11 20V4M17 20V9M3 20h18" />,
    bolt: <path d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z" />,
    cube: (<><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>),
    db: (<><ellipse cx="12" cy="5.5" rx="7" ry="2.8" /><path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" /></>),
    doc: (<><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 9h6M9 13h6M9 17h3" /></>),
    user: (<><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.6-4 3-6 7-6s6.4 2 7 6" /></>),
    clock: (<><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>),
    trend: (<><path d="M3 17l5-5 4 4 8-9" /><path d="M15 7h5v5" /></>),
    globe: (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.6 2.6 3.8 5.6 3.8 9S14.6 18.4 12 21c-2.6-2.6-3.8-5.6-3.8-9S9.4 5.6 12 3Z" /></>),
    briefcase: (<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 13h18" /></>),
    cloud: <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9.5a4.2 4.2 0 0 1-.5 8.5H7Z" />,
    shield: <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" />,
    link: (<><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" /><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" /></>),
    target: (<><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" /></>),
    mobile: (<><rect x="7" y="3" width="10" height="18" rx="2" /><path d="M11 18h2" /></>),
    truck: (<><path d="M3 6h11v10H3zM14 9h4l3 3v4h-7" /><circle cx="7" cy="18" r="1.8" /><circle cx="17" cy="18" r="1.8" /></>),
  };

  return (
    <svg
      className="de-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const capabilities: { icon: IconKey; title: string; text: string; href: string }[] = [
  { icon: "code", title: "Custom Application Development", text: "Design and build enterprise web, mobile and business applications for specific operational requirements.", href: "/solutions/custom-application-development" },
  { icon: "refresh", title: "Application Modernisation", text: "Modernise legacy applications to improve performance, scalability and maintainability.", href: "/solutions/application-modernisation" },
  { icon: "nodes", title: "API & Microservices Engineering", text: "Develop modular services and APIs to support flexible, connected enterprise applications.", href: "/solutions/api-microservices" },
  { icon: "window", title: "Experience & Interface Design", text: "Create intuitive, task-focused digital experiences that improve user productivity.", href: "/solutions/experience-interface-design" },
  { icon: "shieldcheck", title: "Quality Engineering & Automation", text: "Introduce automated testing, performance testing and continuous quality practices.", href: "/solutions/quality-engineering" },
  { icon: "gear", title: "DevOps & Application Lifecycle", text: "Establish deployment, monitoring and release practices for dependable applications.", href: "/solutions/devops-lifecycle" },
];

const solutions: { icon: IconKey; title: string; text: string }[] = [
  { icon: "flow", title: "Enterprise Workflow Applications", text: "Digitise and streamline internal processes." },
  { icon: "people", title: "Customer & Partner Portals", text: "Support self-service, collaboration and information sharing." },
  { icon: "mobile", title: "Sales & Field Applications", text: "Support distributed teams with mobile and web tools." },
  { icon: "refresh", title: "Legacy Application Modernisation", text: "Move critical applications to modern architectures." },
  { icon: "link", title: "Application Integration", text: "Connect with SAP, CRM, ERP and other enterprise systems." },
  { icon: "bolt", title: "Intelligent Application Features", text: "Embed analytics, automation and AI-assisted capabilities." },
];

const steps = [
  { title: "Discover", text: "Understand users, workflows, business goals and operational constraints." },
  { title: "Define", text: "Define solution priorities, user experience and integration requirements." },
  { title: "Build", text: "Develop in iterative sprints with stakeholder reviews." },
  { title: "Test", text: "Test functionality, security and performance across business scenarios." },
  { title: "Release & Improve", text: "Monitor adoption, resolve issues and continue to improve the application." },
];

const benefits: { icon: IconKey; text: string }[] = [
  { icon: "clock", text: "Reduced manual work" },
  { icon: "trend", text: "Improved productivity" },
  { icon: "bolt", text: "Faster process execution" },
  { icon: "user", text: "Better customer experience" },
  { icon: "cube", text: "Greater application flexibility" },
  { icon: "bars", text: "Improved delivery visibility" },
];

const scenarios = [
  { title: "Field Sales Application", text: "Enable teams to capture orders and customer updates in the field.", image: img("MARKET-&-SELL.jpg"), href: "/use-cases/field-sales-application" },
  { title: "Supplier Portal", text: "Simplify supplier collaboration, confirmations and information sharing.", image: img("ILLUSTRATIVE-USE-CASE.jpg"), href: "/use-cases/supplier-portal" },
  { title: "Service Management", text: "Track, manage and resolve service requests with full visibility.", image: img("operate1.jpg"), href: "/use-cases/service-management" },
  { title: "Internal Approval Platform", text: "Automate approvals and notifications across business functions.", image: img("Technology,-Data-and-AI.jpg"), href: "/use-cases/internal-approval-platform" },
  { title: "Legacy Application Modernisation", text: "Rebuild critical legacy applications for the way you work today.", image: img("transform.jpg"), href: "/use-cases/legacy-modernisation" },
  { title: "Operations Dashboard", text: "Bring operational data together for faster, better decisions.", image: img("data-intelligence.jpg"), href: "/use-cases/operations-dashboard" },
];

const why: { icon: IconKey; title: string; text: string }[] = [
  { icon: "briefcase", title: "Business-first approach", text: "We start with workflows and business objectives." },
  { icon: "link", title: "Enterprise integration", text: "We connect with your existing applications and data landscape." },
  { icon: "refresh", title: "Agile delivery", text: "We deliver in short iterations with stakeholder collaboration." },
  { icon: "shieldcheck", title: "Usability and maintainability", text: "We build applications that are easy to use and evolve over time." },
  { icon: "trend", title: "Continuous value optimisation", text: "We support improvement, monitoring and ongoing optimisation." },
];

/* ============================== PAGE ============================== */

export default function DigitalEngineeringPage() {
  const ref = useScrollReveal();

  return (
    <div className="de-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="de-hero" id="top">
          <div className="de-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }} aria-hidden="true"></div>
          <div className="de-hero-overlay" aria-hidden="true"></div>

          <div className="de-hero-inner">
            <div className="de-hero-copy">
              <h1 data-reveal style={delay(0)}>
                Your business has changed. <span>Your applications should keep up.</span>
              </h1>
              <p data-reveal style={delay(1, 120)}>
                Orane helps enterprises design, build and modernise digital applications that solve real
                business problems, integrate with existing systems and deliver measurable value.
              </p>
              <div className="de-actions" data-reveal style={delay(2, 120)}>
                <Link href="/tellusyourproblem#tell-details" className="de-btn de-btn-primary">
                  Tell Us Your Application Challenge <span aria-hidden="true">→</span>
                </Link>
                <Link href="#capabilities" className="de-btn de-btn-outline">
                  Explore Digital Engineering <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
            {/* <p className="de-hero-tagline" data-reveal style={delay(3, 120)}>
              Build | Integrate | Modernise | Innovate
            </p> */}
          </div>
        </section>

        {/* =========================== CAPABILITIES ========================== */}
        <section className="de-section" id="capabilities">
          <div className="de-container">
            <div className="de-cap-wrap">
              <div className="de-cap-intro" data-reveal>
                <h2>Our digital engineering capabilities</h2>
                <p>From idea to execution, we help you build applications that fit your business and scale with your needs.</p>
              </div>

              <div className="de-cap-grid">
                {capabilities.map((c, i) => (
                  <article className="de-cap-card" key={c.title} data-reveal style={delay(i % 2, 80)}>
                    <span className="de-cap-icon"><Icon name={c.icon} /></span>
                    <h3>{c.title}</h3>
                    <p>{c.text}</p>
                    {/* <Link href={c.href} className="de-link">Learn more <span aria-hidden="true">→</span></Link> */}
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ======================== BUILD AROUND THE WORK ==================== */}
        <section className="de-section" id="our-solutions">
          <div className="de-container">
            <div className="de-build">
              <div className="de-build-copy" data-reveal>
                <h2>Build applications around the work that needs to happen.</h2>
                <p>
                  We combine business understanding, modern engineering practices and enterprise integration
                  to deliver applications that improve how people work and how businesses perform.
                </p>
                <Link href="/solutions" className="de-link de-link--lg">
                  Explore our solutions <span aria-hidden="true">→</span>
                </Link>
              </div>
              <div className="de-build-image" data-reveal style={delay(1, 100)}>
                <img src={imgBuild} alt="Orane engineers and business users reviewing an application design" loading="lazy" />
              </div>
            </div>

            <div className="de-solutions" data-reveal>
              <h3>Our solutions include:</h3>
              <ul className="de-solutions-grid">
                {solutions.map((s) => (
                  <li key={s.title}>
                    <span className="de-solution-icon"><Icon name={s.icon} /></span>
                    <div>
                      <strong>{s.title}</strong>
                      <span>{s.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ============================= PROCESS ============================= */}
        <section className="de-section">
          <div className="de-container">
            <div className="de-head de-head--row" data-reveal>
              <h2>From business problem to working application.</h2>
              <div className="de-head-side">
                <p>We follow an agile and structured approach to deliver solutions that work.</p>
                {/* <Link href="/how-we-work" className="de-link">See how it works <span aria-hidden="true">→</span></Link> */}
              </div>
            </div>

            <ol className="de-steps">
              {steps.map((s, i) => (
                <li className="de-step" key={s.title} data-reveal style={delay(i, 90)}>
                  <span className="de-chev"><b>{String(i + 1).padStart(2, "0")}</b>{s.title}</span>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============================ BENEFITS ============================= */}
        <section className="de-section">
          <div className="de-container">
            <div className="de-head de-head--row" data-reveal>
              <h2>Applications that help people work better.</h2>
              <p>Engineering creates value when it improves everyday work and decision-making.</p>
            </div>

            <div className="de-benefit-grid">
              {benefits.map((b, i) => (
                <div className="de-benefit" key={b.text} data-reveal style={delay(i, 70)}>
                  <span className="de-benefit-icon"><Icon name={b.icon} /></span>
                  <span className="de-benefit-text">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ USE CASES ============================ */}
        <section className="de-section">
          <div className="de-container">
            <div className="de-head de-head--row" data-reveal>
              <h2>Practical digital engineering use cases</h2>
              {/* <Link href="/use-cases" className="de-link">Explore more use cases <span aria-hidden="true">→</span></Link> */}
            </div>

            <div className="de-scn-grid">
              {scenarios.map((s, i) => (
                <article className="de-scn-card" key={s.title} data-reveal style={delay(i, 70)}>
                  <div className="de-scn-image">
                    <img src={s.image} alt="" aria-hidden="true" loading="lazy" />
                  </div>
                  <div className="de-scn-body">
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                    {/* <Link href={s.href} className="de-link">See how we help <span aria-hidden="true">→</span></Link> */}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================== WHY ORANE ========================== */}
        <section className="de-section">
          <div className="de-container">
            <div className="de-why">
              <div className="de-why-head" data-reveal>
                <h2>Why Orane for digital engineering?</h2>
                <p>We build solutions that solve the right problem — not just the technology.</p>
              </div>
              <ul className="de-why-list">
                {why.map((w, i) => (
                  <li className="de-why-item" key={w.title} data-reveal style={delay(i, 80)}>
                    <span className="de-why-icon"><Icon name={w.icon} /></span>
                    <h3>{w.title}</h3>
                    <p>{w.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ============================ FINAL CTA ============================ */}
        <section className="de-cta" id="contact">
          <img className="de-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="de-cta-copy">
            <span className="de-cta-eyebrow" data-reveal>NEED THINGS TO WORK BETTER?</span>
            <h2 data-reveal style={delay(1, 120)}>Let&apos;s build the right solution.</h2>
            <p data-reveal style={delay(2, 120)}>
              Tell us which process needs a workaround, where teams struggle or what your users need to do
              more easily. We&apos;ll help you design and build a digital solution that creates real business value.
            </p>
            <div className="de-actions" data-reveal style={delay(3, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="de-btn de-btn-primary">
                Tell Us Your Application Challenge <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}